import UserAuthenticationService from './UserAuthenticationService';
import { makeRequest } from 'js/services/Network';
import AnonymousId from 'js/services/AnonymousId';

/**
 * Wrapper for Facebook javascript API
 * Based on info from: https://developers.facebook.com/docs/javascript/quickstart
 *
 * @class FacebookAuth
 */
class FacebookAuth extends UserAuthenticationService {
  facebookLoginStatus = null;
  isInitialized = new Promise((resolve, reject) => {
    resolve();
  });

  /**
   * Get a Promise which resolves with the current login status of the user.
   * https://developers.facebook.com/docs/reference/javascript/FB.getLoginStatus/
   *
   * @param {boolean} [refresh=false] If true then the login status will be loaded from facebook (even if login status already checked)
   * @returns Promise
   * @memberof FacebookAuth
   */
  async getLoginStatus(refresh = false) {
    await this.isInitialized;

    if (refresh || !this.facebookLoginStatus) {
      return await new Promise((resolve, reject) => {
        FB.getLoginStatus(response => {
          this.facebookLoginStatus = response;
          resolve(response);
        });
      });
    } else {
      return Promise.resolve(this.facebookLoginStatus);
    }
  }

  /**
   * Trigger Facebook login, then register the cookie'd token with the application via the facebook auth callback
   * https://developers.facebook.com/docs/reference/javascript/FB.login/v2.9
   *
   * @param {string} [scope='email']
   * @returns Promise
   * @memberof FacebookAuth
   */
  async login(scope = 'email', data = {}) {

    this.facebookLoginStatus = await this.loginReq(scope);

    return (await makeRequest('facebook-login', null, {
      fb_token: this.facebookLoginStatus.authResponse.accessToken,
      anonymous_id: AnonymousId,
      token: (document.getElementsByName('token')[0]) ? document.getElementsByName('token')[0].value : null,
      ...data,
    })).data;
  }

  /**
   * Trigger Facebook logout
   * https://developers.facebook.com/docs/reference/javascript/FB.logout/
   *
   * @returns
   * @memberof FacebookAuth
   */
  async logout() {
    await this.isInitialized;

    if (this.facebookLoginStatus && this.facebookLoginStatus.authResponse) {
      await new Promise((resolve, reject) => {
        this.facebookLoginStatus = null;
        FB.logout(() => resolve());
      });
    }

    return await makeRequest('logout');
  }

  /**
   * Trigger Facebook login, then register the cookie'd token with the application via the facebook auth callback
   * https://developers.facebook.com/docs/reference/javascript/FB.login/v2.9
   *
   * @param {string} [scope='email']
   * @returns Promise
   * @memberof FacebookAuth
   */
  async loginReq(scope) {
    await this.isInitialized;
    return new Promise((resolve, reject) => {
      FB.login(function(response) {
        if (response.authResponse) {
          resolve(response);
        } else {
          reject(response);
        }
      },{ scope: scope, return_scopes: true });
    });
  }
}

export default new FacebookAuth();
